<app-confirm-dialog
  data-testid="om-create-dialog"
  titleId="om-create-title"
  [visible]="true"
  [icon]="recordOnly() ? 'visibility' : 'add'"
  iconTon="akzent"
  confirmColor="primary"
  [wide]="!recordOnly()"
  [maxWidthRem]="recordOnly() ? 35 : null"
  [title]="title() | translate"
  [message]="message() | translate: { name: library()?.name }"
  [confirmLabel]="submitLabel() | translate"
  [cancelLabel]="'objectMappings.create.cancel' | translate"
  [showCancel]="recording() || way() !== 'tox'"
  [busy]="saving()"
  [confirmDisabled]="!canSubmit()"
  (confirmed)="submit()"
  (cancelled)="cancel()"
>
  @if (!recordOnly()) {
    <!-- prettier-ignore -->
    <span message>{{ ' ' }}<code class="omc-code">{{ 'objectMappings.create.messageCode' | translate }}</code>.</span>
  }
  <form data-testid="om-create-form" class="omc-form" (submit)="$event.preventDefault(); submit()">
    @if (!recordOnly()) {
      <div class="omc-field">
        <label class="omc-label" for="om-create-name">
          {{ 'objectMappings.create.name' | translate }}
          <span class="omc-required" aria-hidden="true">*</span>
        </label>
        <input
          #nameInput
          id="om-create-name"
          class="omc-input omc-input--mono"
          required
          data-testid="om-create-name"
          autocomplete="off"
          [class.omc-input--invalid]="showNameError()"
          [placeholder]="'objectMappings.create.namePlaceholder' | translate"
          [value]="name()"
          [disabled]="locked()"
          [attr.maxlength]="fieldLengths.NAME_MAX"
          [attr.aria-invalid]="showNameError() ? 'true' : null"
          aria-describedby="om-create-name-hint"
          (input)="onNameInput($event)"
          (blur)="nameTouched.set(true)"
        />
        @if (showNameError()) {
          <p id="om-create-name-hint" class="omc-hint omc-hint--error" data-testid="om-create-invalid">
            {{ nameError()! | translate }}
          </p>
        } @else {
          <p id="om-create-name-hint" class="omc-hint" data-testid="om-create-name-hint">
            {{ 'objectMappings.create.nameHint' | translate }}
          </p>
        }
      </div>

      <div class="omc-row">
        <div class="omc-field">
          <label class="omc-label" for="om-create-engine">{{ 'objectMappings.technology.label' | translate }}</label>
          <select
            id="om-create-engine"
            class="omc-input omc-select"
            data-testid="om-create-engine"
            [disabled]="locked()"
            (change)="chooseEngine($any($event.target).value)"
          >
            @for (option of engines; track option) {
              <option [value]="option" [selected]="option === chosenEngine()">
                {{ 'objectMappings.technology.' + option | translate }}
              </option>
            }
          </select>
        </div>
        <div class="omc-field">
          <label class="omc-label" for="om-create-address">{{ 'objectMappings.create.address' | translate }}</label>
          <input
            id="om-create-address"
            class="omc-input"
            data-testid="om-create-address"
            autocomplete="off"
            [placeholder]="'objectMappings.create.addressPlaceholder' | translate"
            [value]="address()"
            [disabled]="locked()"
            [attr.maxlength]="fieldLengths.OBJECT_MAPPING_ADDRESS_MAX"
            (input)="onAddressInput($event)"
          />
        </div>
      </div>

      <div class="omc-ways" role="radiogroup" [attr.aria-label]="'objectMappings.create.ways.legend' | translate">
        @for (option of ways; track option.id) {
          <div
            class="omc-way"
            role="radio"
            [class.omc-way--active]="way() === option.id"
            [class.omc-way--disabled]="locked()"
            [attr.data-testid]="'om-create-way-' + option.id"
            [attr.aria-checked]="way() === option.id"
            [attr.aria-disabled]="locked() ? 'true' : null"
            [attr.tabindex]="way() === option.id ? 0 : -1"
            (click)="chooseWay(option.id)"
            (keydown)="onWayKeydown($event, option.id)"
          >
            <span class="omc-way__icon" aria-hidden="true">
              <mat-icon>{{ option.icon }}</mat-icon>
            </span>
            <span class="omc-way__text">
              <span class="omc-way__label">
                {{ 'objectMappings.create.ways.' + wayKey(option.id) + '.label' | translate }}
              </span>
              <span class="omc-way__desc">
                {{ 'objectMappings.create.ways.' + wayKey(option.id) + '.desc' | translate }}
              </span>
            </span>
            <span class="omc-way__dot" aria-hidden="true"></span>
          </div>
        }
      </div>
    }

    @if (recording()) {
      <div class="omc-record">
        <app-runner-target-select
          data-testid="om-create-runner"
          labelKey="objectMappings.record.runnerLabel"
          [projectId]="projectId()"
          [recordingProjectId]="projectId()"
          [recordingEngine]="engine()"
          (targetChange)="target.set($event)"
          (connect)="cancel()"
        />
        @switch (engine()) {
          @case ('windows') {
            <p class="omc-hint">{{ 'objectMappings.record.scanRunnerHint' | translate }}</p>
            <mat-form-field appearance="fill" floatLabel="always" class="omc-wide tc-field tc-field--uppercase-label">
              <mat-label>{{ 'objectMappings.record.program' | translate }}</mat-label>
              <input
                matInput
                data-testid="om-create-program"
                autocomplete="off"
                [placeholder]="'objectMappings.record.programPlaceholder' | translate"
                [value]="startProgram()"
                [attr.maxlength]="fieldLengths.START_PROGRAM_MAX"
                (input)="onProgramInput($event)"
              />
              <mat-hint>{{ 'objectMappings.record.programHint' | translate }}</mat-hint>
            </mat-form-field>
          }
          @case ('sap') {
            <p class="omc-notice" data-testid="om-create-sap-hint">
              <mat-icon aria-hidden="true">info</mat-icon>
              <span>{{ 'objectMappings.record.sapHint' | translate }}</span>
            </p>
          }
          @default {
            <p class="omc-hint">{{ 'objectMappings.record.runnerHint' | translate }}</p>
            <mat-form-field appearance="fill" floatLabel="always" class="omc-wide tc-field tc-field--uppercase-label">
              <mat-label>{{ startUrlLabel() | translate }}</mat-label>
              <input
                #urlInput
                matInput
                required
                type="url"
                data-testid="om-create-url"
                autocomplete="url"
                [placeholder]="'objectMappings.record.startUrlPlaceholder' | translate"
                [value]="startUrl()"
                [attr.maxlength]="fieldLengths.URL_MAX"
                [attr.aria-describedby]="showUrlInvalid() ? 'om-create-url-invalid' : null"
                (input)="onUrlInput($event)"
              />
            </mat-form-field>
            @if (showUrlInvalid()) {
              <p id="om-create-url-invalid" class="omc-hint omc-hint--error" data-testid="om-create-url-invalid">
                {{ 'objectMappings.record.invalidUrl' | translate }}
              </p>
            }
          }
        }
      </div>
    } @else if (way() === 'tox') {
      <p class="omc-notice" data-testid="om-create-tox-hint">
        <mat-icon aria-hidden="true">info</mat-icon>
        <span>
          {{ 'objectMappings.create.ways.tox.hint' | translate }}
          <a
            data-testid="om-create-tox-settings-link"
            [routerLink]="['/projects', projectId(), 'edit']"
            fragment="classic-import"
          >
            {{ 'objectMappings.create.ways.tox.settingsLink' | translate }}
          </a>
        </span>
      </p>
    }
  </form>

  @if (error(); as message) {
    <div class="omc-error" role="alert" data-testid="om-create-error">
      <p class="omc-hint omc-hint--error">
        {{ message }}
        @if (activeJobId(); as jobId) {
          <a
            data-testid="om-create-to-recording"
            [routerLink]="['/projects', projectId(), 'object-mappings', mapping()?.id, 'recording', jobId]"
          >
            {{ 'objectMappings.record.toRecording' | translate }}
          </a>
        }
      </p>
      @if (createdLibrary() && !recordOnly()) {
        <button mat-stroked-button type="button" data-testid="om-create-to-library" (click)="toLibrary()">
          {{ 'objectMappings.record.toLibrary' | translate }}
        </button>
      }
    </div>
  }
</app-confirm-dialog>
